import React, { useState, useEffect, useContext } from 'react';
import { Link, useLocation } from 'react-router-dom';
import axios from 'axios';
import { AuthContext } from '@/App';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Card } from '@/components/ui/card';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '@/components/ui/dialog';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { toast } from 'sonner';
import { MapPin, Plus, Search, Edit } from 'lucide-react';
import QRScanner from '@/components/QRScanner';
import { useNavigate } from 'react-router-dom';
import { hasContent } from '@/utils/fillDisplay';
import ScanButton from '@/components/ScanButton';
import UnknownCodeDialog from '@/components/UnknownCodeDialog';
import { resolveScan, ScanResultType, ScanContext } from '@/utils/scanResolver';

const StandortePage = () => {
  const { user, API } = useContext(AuthContext);
  const navigate = useNavigate();
  const location = useLocation();
  const [standorte, setStandorte] = useState([]);
  const [filteredStandorte, setFilteredStandorte] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [showCreateDialog, setShowCreateDialog] = useState(false);
  const [showEditDialog, setShowEditDialog] = useState(false);
  const [editingStandort, setEditingStandort] = useState(null);
  const [showScanner, setShowScanner] = useState(false);
  const [showUnknownCodeDialog, setShowUnknownCodeDialog] = useState(false);
  const [scannedUnknownCode, setScannedUnknownCode] = useState('');
  const [formData, setFormData] = useState({
    code: '',
    qr_value: '',
    name: '',
    key_number: '',
    street: '',
    zip: '',
    city: '',
    district: '',
    location_description: '',
    housing_type: '1er',
    refill_cycle_days: 90,
    contact_name: '',
    contact_phone: '',
    contact_email: '',
    notes: '',
    latitude: null,
    longitude: null,
    status: 'aktiv',
    damage_note: '',
    task: '',
  });

  useEffect(() => {
    fetchStandorte();
  }, []);

  useEffect(() => {
    filterStandorte();
  }, [searchTerm, statusFilter, standorte]);

  useEffect(() => {
    // Check if we should open create dialog with scanned code
    if (location.state?.openCreate && location.state?.scannedCode) {
      setFormData({
        ...formData,
        code: location.state.scannedCode,
        qr_value: location.state.scannedCode,
      });
      setShowCreateDialog(true);
      toast.info(`Code gescannt: ${location.state.scannedCode}`);
      // Clear the state
      navigate(location.pathname, { replace: true, state: {} });
    }
  }, [location.state]);

  const fetchStandorte = async () => {
    try {
      const response = await axios.get(`${API}/standorte`);
      setStandorte(response.data || []);
    } catch (error) {
      // Nur bei echten Serverfehlern einen Toast zeigen
      if (error.response?.status >= 500) {
        toast.error('Serverfehler beim Laden der Standorte');
      } else if (error.response?.status === 403) {
        toast.error('Keine Berechtigung für Standorte');
      }
      // Bei anderen Fehlern: leere Liste anzeigen ohne Toast
      setStandorte([]);
      console.error('Standorte laden:', error.response?.status || error.message);
    } finally {
      setLoading(false);
    }
  };

  const filterStandorte = () => {
    let filtered = standorte;

    if (statusFilter !== 'all') {
      filtered = filtered.filter((s) => s.status === statusFilter);
    }

    if (searchTerm) {
      const term = searchTerm.toLowerCase();
      filtered = filtered.filter(
        (s) =>
          s.name.toLowerCase().includes(term) ||
          s.code.toLowerCase().includes(term) ||
          s.city.toLowerCase().includes(term) ||
          s.zip.includes(term)
      );
    }

    setFilteredStandorte(filtered);
  };

  const handleCreateStandort = async (e) => {
    e.preventDefault();
    try {
      await axios.post(`${API}/standorte`, formData);
      toast.success('Standort erstellt!');
      setShowCreateDialog(false);
      fetchStandorte();
      setFormData({
        code: '',
        qr_value: '',
        name: '',
        street: '',
        zip: '',
        city: '',
        location_description: '',
        housing_type: '1er',
        refill_cycle_days: 90,
        contact_name: '',
        contact_phone: '',
        contact_email: '',
        notes: '',
        latitude: null,
        longitude: null,
        status: 'aktiv',
      });
    } catch (error) {
      const detail = error.response?.data?.detail;
      // Spezifische Fehlermeldungen für Duplikate
      if (detail === 'LOCATION_CODE_EXISTS') {
        toast.error('Standortcode bereits vergeben. Bitte wählen Sie einen anderen Code.');
      } else {
        toast.error(detail || 'Fehler beim Erstellen');
      }
    }
  };

  const handleQRScan = async (qr) => {
    setShowScanner(false);
    
    // Normalisiere den gescannten Code (nur trim, KEIN parseInt)
    const normalizedCode = qr.trim();
    console.log(`[StandortePage-Scan] Code gescannt: '${normalizedCode}'`);
    
    // Check if we're in create or edit mode - dann Code nur übernehmen
    if (showCreateDialog || editingStandort) {
      setFormData({ ...formData, code: normalizedCode, qr_value: normalizedCode });
      toast.success(`Code gescannt: ${normalizedCode}`);
      
      // Reopen the dialog
      if (editingStandort) {
        setShowEditDialog(true);
      } else {
        setShowCreateDialog(true);
      }
      return;
    }
    
    // UNIVERSELLER SCAN: Nutze zentralen Resolver
    try {
      const result = await resolveScan({
        qrValue: normalizedCode,
        api: API,
        user,
        context: ScanContext.UNIVERSAL
      });

      console.log(`[StandortePage-Scan] Resolver-Ergebnis:`, result);

      // Fall 1: Standort gefunden
      if (result.type === ScanResultType.STANDORT) {
        toast.success(result.message);
        navigate(`/standorte/scan/${encodeURIComponent(normalizedCode)}`);
        return;
      }
      
      // Fall 2: Automat gefunden
      if (result.type === ScanResultType.AUTOMAT) {
        toast.info(`Automat "${result.data.code}" gefunden - wechsle zu Automaten`);
        navigate(result.navigateTo);
        return;
      }
      
      // Fall 3: Fehler (z.B. Berechtigung)
      if (result.type === ScanResultType.ERROR) {
        toast.error(result.message);
        return;
      }
      
      // Fall 4: Nicht gefunden - EINHEITLICHER UNKNOWN-FLOW
      if (result.type === ScanResultType.UNKNOWN) {
        console.log(`[StandortePage-Scan] Code nicht gefunden, zeige Erstellen-Dialog`);
        setScannedUnknownCode(normalizedCode);
        setShowUnknownCodeDialog(true);
      }
    } catch (error) {
      console.error('[StandortePage-Scan] Fehler:', error);
      toast.error('Fehler beim Verarbeiten des Scans');
    }
  };

  // Handler für Unknown Code Dialog
  const handleCreateFromUnknown = (type) => {
    setShowUnknownCodeDialog(false);
    if (type === 'standort') {
      // Bei Standort: Dialog direkt öffnen mit vorausgefülltem Code
      setFormData({
        ...formData,
        code: scannedUnknownCode,
        qr_value: scannedUnknownCode,
      });
      setShowCreateDialog(true);
    } else {
      navigate('/automaten', { state: { openCreate: true, scannedCode: scannedUnknownCode } });
    }
  };

  const handleEdit = (standort) => {
    setEditingStandort(standort);
    setFormData({
      code: standort.code,
      qr_value: standort.qr_value,
      name: standort.name,
      street: standort.street,
      zip: standort.zip,
      city: standort.city,
      location_description: standort.location_description,
      housing_type: standort.housing_type,
      refill_cycle_days: standort.refill_cycle_days,
      contact_name: standort.contact_name,
      contact_phone: standort.contact_phone,
      contact_email: standort.contact_email || '',
      notes: standort.notes || '',
      latitude: standort.latitude,
      longitude: standort.longitude,
      status: standort.status,
    });
    setShowEditDialog(true);
  };

  const handleUpdate = async (e) => {
    e.preventDefault();
    try {
      await axios.put(`${API}/standorte/${editingStandort.id}`, formData);
      toast.success('Standort aktualisiert!');
      setShowEditDialog(false);
      setEditingStandort(null);
      fetchStandorte();
    } catch (error) {
      const detail = error.response?.data?.detail;
      // Spezifische Fehlermeldungen für Duplikate
      if (detail === 'LOCATION_CODE_EXISTS') {
        toast.error('Standortcode bereits vergeben. Bitte wählen Sie einen anderen Code.');
      } else {
        toast.error(detail || 'Fehler beim Aktualisieren');
      }
    }
  };

  if (loading) {
    return <div className="flex justify-center items-center h-64">Laden...</div>;
  }

  return (
    <div className="space-y-6 animate-slide-in" data-testid="standorte-page">
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
        <div>
          <h1 className="text-4xl font-bold mb-2" style={{ fontFamily: 'Space Grotesk' }}>
            Standorte
          </h1>
          <p className="text-slate-600">Verwalte deine Standorte</p>
        </div>
        <div className="flex space-x-2">
          <ScanButton
            onClick={() => setShowScanner(true)}
            variant="outline"
            data-testid="scan-standort-button"
          />
          {user?.role === 'admin' && (
            <Dialog open={showCreateDialog} onOpenChange={setShowCreateDialog}>
              <DialogTrigger asChild>
                <Button className="flex items-center space-x-2 bg-gradient-to-r from-blue-500 to-indigo-600" data-testid="create-standort-button">
                  <Plus size={20} />
                  <span>Neu</span>
                </Button>
              </DialogTrigger>
              <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
                <DialogHeader>
                  <DialogTitle>Neuen Standort erstellen</DialogTitle>
                </DialogHeader>
                <form onSubmit={handleCreateStandort} className="space-y-4">
                  <div>
                    <Label>Code (wird auch für QR-Code verwendet)</Label>
                    <div className="flex gap-2">
                      <Input
                        value={formData.code}
                        onChange={(e) => setFormData({ ...formData, code: e.target.value, qr_value: e.target.value })}
                        required
                        placeholder="ST-0001"
                        className="flex-1"
                      />
                      <ScanButton
                        onClick={() => {
                          setShowCreateDialog(false);
                          setShowScanner(true);
                        }}
                        variant="outline"
                        size="sm"
                        data-testid="scan-standort-code-button"
                      />
                    </div>
                    <p className="text-xs text-slate-500 mt-1">
                      Manuell eingeben oder per Kamera scannen
                    </p>
                  </div>

                  <div>
                    <Label>Name</Label>
                    <Input
                      value={formData.name}
                      onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                      required
                      placeholder="Bäckerei Müller"
                    />
                  </div>

                  <div>
                    <Label>Schlüsselnummer (optional)</Label>
                    <Input
                      value={formData.key_number}
                      onChange={(e) => setFormData({ ...formData, key_number: e.target.value })}
                      placeholder="z.B. 12345"
                    />
                  </div>

                  <div className="grid grid-cols-3 gap-4">
                    <div className="col-span-2">
                      <Label>Straße</Label>
                      <Input
                        value={formData.street}
                        onChange={(e) => setFormData({ ...formData, street: e.target.value })}
                        required
                      />
                    </div>
                    <div>
                      <Label>PLZ</Label>
                      <Input
                        value={formData.zip}
                        onChange={(e) => setFormData({ ...formData, zip: e.target.value })}
                        required
                      />
                    </div>
                  </div>

                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <Label>Stadt</Label>
                      <Input
                        value={formData.city}
                        onChange={(e) => setFormData({ ...formData, city: e.target.value })}
                        required
                      />
                    </div>
                    <div>
                      <Label>Ortsteil (optional)</Label>
                      <Input
                        value={formData.district}
                        onChange={(e) => setFormData({ ...formData, district: e.target.value })}
                        placeholder="z.B. Altstadt"
                      />
                    </div>
                  </div>

                  <div>
                    <Label>Standortbeschreibung</Label>
                    <Input
                      value={formData.location_description}
                      onChange={(e) => setFormData({ ...formData, location_description: e.target.value })}
                      required
                      placeholder="Am Eingang rechts neben der Tür"
                    />
                  </div>

                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <Label>Gehäusetyp</Label>
                      <Select value={formData.housing_type} onValueChange={(value) => setFormData({ ...formData, housing_type: value })}>
                        <SelectTrigger>
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="1er">1er</SelectItem>
                          <SelectItem value="2er">2er</SelectItem>
                          <SelectItem value="3er">3er</SelectItem>
                          <SelectItem value="4er">4er</SelectItem>
                          <SelectItem value="5er">5er</SelectItem>
                          <SelectItem value="Goliath">Goliath</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                    <div>
                      <Label>Befüllzyklus (Tage)</Label>
                      <Input
                        type="number"
                        value={formData.refill_cycle_days}
                        onChange={(e) => setFormData({ ...formData, refill_cycle_days: parseInt(e.target.value) })}
                        required
                      />
                    </div>
                  </div>

                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <Label>Kontaktperson</Label>
                      <Input
                        value={formData.contact_name}
                        onChange={(e) => setFormData({ ...formData, contact_name: e.target.value })}
                        required
                      />
                    </div>
                    <div>
                      <Label>Telefon</Label>
                      <Input
                        value={formData.contact_phone}
                        onChange={(e) => setFormData({ ...formData, contact_phone: e.target.value })}
                        required
                      />
                    </div>
                  </div>

                  <Button type="submit" className="w-full" data-testid="submit-create-standort">
                    Erstellen
                  </Button>
                </form>
              </DialogContent>
            </Dialog>
          )}

          {/* Edit Dialog */}
          {user?.role === 'admin' && (
            <Dialog open={showEditDialog} onOpenChange={setShowEditDialog}>
              <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
                <DialogHeader>
                  <DialogTitle>Standort bearbeiten</DialogTitle>
                </DialogHeader>
                <form onSubmit={handleUpdate} className="space-y-4">
                  <div>
                    <Label>Code (wird auch für QR-Code verwendet)</Label>
                    <div className="flex gap-2">
                      <Input
                        value={formData.code}
                        onChange={(e) => setFormData({ ...formData, code: e.target.value, qr_value: e.target.value })}
                        required
                        placeholder="ST-0001"
                        className="flex-1"
                      />
                      <ScanButton
                        onClick={() => {
                          setShowEditDialog(false);
                          setShowScanner(true);
                        }}
                        variant="outline"
                        size="sm"
                        data-testid="scan-standort-code-button"
                      />
                    </div>
                    <p className="text-xs text-slate-500 mt-1">
                      Manuell eingeben oder per Kamera scannen
                    </p>
                  </div>

                  <div>
                    <Label>Name</Label>
                    <Input
                      value={formData.name}
                      onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                      required
                      placeholder="Bäckerei Müller"
                    />
                  </div>

                  <div>
                    <Label>Schlüsselnummer (optional)</Label>
                    <Input
                      value={formData.key_number}
                      onChange={(e) => setFormData({ ...formData, key_number: e.target.value })}
                      placeholder="z.B. 12345"
                    />
                  </div>

                  <div className="grid grid-cols-3 gap-4">
                    <div className="col-span-2">
                      <Label>Straße</Label>
                      <Input
                        value={formData.street}
                        onChange={(e) => setFormData({ ...formData, street: e.target.value })}
                        required
                      />
                    </div>
                    <div>
                      <Label>PLZ</Label>
                      <Input
                        value={formData.zip}
                        onChange={(e) => setFormData({ ...formData, zip: e.target.value })}
                        required
                      />
                    </div>
                  </div>

                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <Label>Stadt</Label>
                      <Input
                        value={formData.city}
                        onChange={(e) => setFormData({ ...formData, city: e.target.value })}
                        required
                      />
                    </div>
                    <div>
                      <Label>Ortsteil (optional)</Label>
                      <Input
                        value={formData.district}
                        onChange={(e) => setFormData({ ...formData, district: e.target.value })}
                        placeholder="z.B. Altstadt"
                      />
                    </div>
                  </div>

                  <div>
                    <Label>Standortbeschreibung</Label>
                    <Input
                      value={formData.location_description}
                      onChange={(e) => setFormData({ ...formData, location_description: e.target.value })}
                      required
                      placeholder="Am Eingang rechts neben der Tür"
                    />
                  </div>

                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <Label>Gehäusetyp</Label>
                      <Select value={formData.housing_type} onValueChange={(value) => setFormData({ ...formData, housing_type: value })}>
                        <SelectTrigger>
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="1er">1er</SelectItem>
                          <SelectItem value="2er">2er</SelectItem>
                          <SelectItem value="3er">3er</SelectItem>
                          <SelectItem value="4er">4er</SelectItem>
                          <SelectItem value="5er">5er</SelectItem>
                          <SelectItem value="Goliath">Goliath</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                    <div>
                      <Label>Befüllzyklus (Tage)</Label>
                      <Input
                        type="number"
                        value={formData.refill_cycle_days}
                        onChange={(e) => setFormData({ ...formData, refill_cycle_days: parseInt(e.target.value) })}
                        required
                      />
                    </div>
                  </div>

                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <Label>Kontaktperson</Label>
                      <Input
                        value={formData.contact_name}
                        onChange={(e) => setFormData({ ...formData, contact_name: e.target.value })}
                        required
                      />
                    </div>
                    <div>
                      <Label>Telefon</Label>
                      <Input
                        value={formData.contact_phone}
                        onChange={(e) => setFormData({ ...formData, contact_phone: e.target.value })}
                        required
                      />
                    </div>
                  </div>

                  <div>
                    <Label>Status</Label>
                    <Select value={formData.status} onValueChange={(value) => setFormData({ ...formData, status: value })}>
                      <SelectTrigger>
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="aktiv">Aktiv</SelectItem>
                        <SelectItem value="LEER">LEER</SelectItem>
                        <SelectItem value="Schaden">Schaden</SelectItem>
                        <SelectItem value="veraltet">Veraltet</SelectItem>
                        <SelectItem value="entfernt">Entfernt</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>

                  {/* Schadensbemerkung (nur wenn Status = Schaden) */}
                  {formData.status === 'Schaden' && (
                    <div>
                      <Label>Schadensbemerkung (Pflichtfeld)</Label>
                      <Textarea
                        value={formData.damage_note}
                        onChange={(e) => setFormData({ ...formData, damage_note: e.target.value })}
                        required
                        placeholder="Beschreiben Sie den Schaden..."
                        rows={3}
                      />
                    </div>
                  )}

                  <div>
                    <Label>Aufgabe für nächsten Besuch (optional)</Label>
                    <Textarea
                      value={formData.task}
                      onChange={(e) => setFormData({ ...formData, task: e.target.value })}
                      placeholder="z.B. Schloss reparieren, Reinigung, etc..."
                      rows={2}
                    />
                    <p className="text-xs text-slate-500 mt-1">
                      Wird in der Tourenplanung und im Dashboard angezeigt
                    </p>
                  </div>

                  <Button type="submit" className="w-full" data-testid="submit-update-standort">
                    Aktualisieren
                  </Button>
                </form>
              </DialogContent>
            </Dialog>
          )}
        </div>
      </div>

      {/* Filters */}
      <Card className="p-4">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div className="relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" size={20} />
            <Input
              placeholder="Suche nach Name, Code, Stadt oder PLZ..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="pl-11"
              data-testid="search-input"
            />
          </div>
          <Select value={statusFilter} onValueChange={setStatusFilter}>
            <SelectTrigger data-testid="status-filter">
              <SelectValue placeholder="Status filtern" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">Alle Status</SelectItem>
              <SelectItem value="aktiv">Aktiv</SelectItem>
              <SelectItem value="veraltet">Veraltet</SelectItem>
              <SelectItem value="entfernt">Entfernt</SelectItem>
            </SelectContent>
          </Select>
        </div>
      </Card>

      {/* Standorte Grid */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
        {filteredStandorte.map((standort) => (
          <Card key={standort.id} className="p-6 card-hover relative" data-testid={`standort-card-${standort.code}`}>
            {user?.role === 'admin' && (
              <Button
                onClick={(e) => {
                  e.preventDefault();
                  handleEdit(standort);
                }}
                variant="outline"
                size="sm"
                className="absolute top-4 right-4 z-10"
                data-testid={`edit-standort-${standort.code}`}
              >
                <Edit size={16} />
              </Button>
            )}
            <Link to={`/standorte/${standort.id}`} className="block">
              <div className="flex items-start space-x-3 mb-4">
                <div className="bg-blue-100 p-3 rounded-xl">
                  <MapPin className="text-blue-600" size={24} />
                </div>
                <div className="flex-1">
                  <h3 className="font-bold text-lg mb-1">{standort.name}</h3>
                  <div className="flex items-center gap-2">
                    <p className="text-sm text-slate-500">{standort.code}</p>
                    {standort.key_number && (
                      <span className="text-sm font-semibold text-blue-600">
                        🔑 {standort.key_number}
                      </span>
                    )}
                  </div>
                </div>
              </div>
              <div className="space-y-2 text-sm text-slate-600">
                {/* Adresse + Nächste Leerung */}
                <div className="flex justify-between items-start">
                  <div>
                    <p>{standort.street}</p>
                    <p>
                      {standort.zip} {standort.city}
                      {standort.district && <span className="text-slate-400"> ({standort.district})</span>}
                    </p>
                  </div>
                  {/* P1: Nächste Leerung */}
                  {standort.days_until_next !== null && standort.days_until_next !== undefined && (
                    <span 
                      className={`text-xs font-semibold px-2 py-1 rounded-full whitespace-nowrap ${
                        standort.days_until_next >= 0 
                          ? 'bg-green-100 text-green-700' 
                          : 'bg-red-100 text-red-700'
                      }`}
                      title="Tage bis zur nächsten Leerung"
                    >
                      {standort.days_until_next >= 0 ? `${standort.days_until_next} Tage` : `${standort.days_until_next} Tage`}
                    </span>
                  )}
                </div>
                {standort.task && (
                  <div className="bg-purple-50 border border-purple-200 rounded p-2 text-xs">
                    <span className="font-semibold text-purple-700">📋 Aufgabe:</span> {standort.task}
                  </div>
                )}
                <div className="flex justify-between items-center pt-2 border-t">
                  <span className="font-medium">{standort.housing_type}</span>
                  {/* Einheitliche Defektlogik: location_is_defect ODER Status "Schaden"/"defekt gemeldet" */}
                  {(standort.location_is_defect || standort.status === 'Schaden' || standort.status === 'defekt gemeldet') ? (
                    <span className="px-2 py-1 rounded-full text-xs font-medium bg-red-100 text-red-700 flex items-center">
                      ⚠️ defekt
                    </span>
                  ) : (
                    <span
                      className={`px-2 py-1 rounded-full text-xs font-medium ${
                        standort.status === 'aktiv'
                          ? 'bg-green-100 text-green-700'
                          : standort.status === 'LEER'
                          ? 'bg-red-100 text-red-700'
                          : standort.status === 'veraltet'
                          ? 'bg-amber-100 text-amber-700'
                          : 'bg-slate-100 text-slate-700'
                      }`}
                    >
                      {standort.status}
                    </span>
                  )}
                </div>
                
                {/* Teil B: Inhalt (Produkte pro Schacht) - Korrekte Nummerierung 1-5, keine "+weitere" */}
                <div className="pt-2 border-t min-h-[100px]">
                  {standort.content_preview && (Array.isArray(standort.content_preview) ? standort.content_preview.length > 0 : Object.keys(standort.content_preview).length > 0) ? (
                    <div className="space-y-1">
                      {/* Unterstütze beide Formate: Array [{slot_number, product_name}] und Objekt {1: "Name"} */}
                      {(Array.isArray(standort.content_preview) 
                        ? standort.content_preview.slice(0, 5)
                        : Object.entries(standort.content_preview).slice(0, 5).map(([slot, name]) => ({ slot_number: parseInt(slot), product_name: name }))
                      ).map((item, idx) => (
                        <div key={idx} className="flex justify-between text-xs">
                          <span className="text-slate-500">Schacht {item.slot_number || idx + 1}:</span>
                          <span className={`font-medium truncate max-w-[120px] ${item.product_name ? 'text-slate-700' : 'text-red-500'}`}>
                            {item.product_name || 'Leer'}
                          </span>
                        </div>
                      ))}
                    </div>
                  ) : (
                    <span className="text-xs text-red-500 font-semibold">KEIN INHALT</span>
                  )}
                </div>
                
                {/* Gesamtumsatz pro Standort */}
                {standort.status === 'aktiv' && typeof standort.total_revenue === 'number' && (
                  <div className="flex justify-between items-center pt-2 border-t mt-2">
                    <span className="text-slate-500 text-sm">Gesamtumsatz:</span>
                    <span className={`font-semibold ${standort.total_revenue > 0 ? 'text-green-600' : 'text-slate-400'}`}>
                      {standort.total_revenue.toFixed(2)} €
                    </span>
                  </div>
                )}
              </div>
            </Link>
          </Card>
        ))}
      </div>

      {filteredStandorte.length === 0 && !loading && (
        <Card className="p-12 text-center">
          <MapPin className="mx-auto h-12 w-12 text-slate-300 mb-4" />
          <h3 className="text-lg font-medium text-slate-700 mb-2">
            {standorte.length === 0 ? 'Noch keine Standorte vorhanden' : 'Keine Standorte gefunden'}
          </h3>
          <p className="text-slate-500 mb-4">
            {standorte.length === 0 
              ? 'Erstellen Sie Ihren ersten Standort, um Automaten platzieren zu können.'
              : 'Versuchen Sie einen anderen Suchbegriff oder Filter.'}
          </p>
          {standorte.length === 0 && user?.role === 'admin' && (
            <Button onClick={() => setShowCreateDialog(true)} data-testid="create-first-standort">
              <Plus className="mr-2 h-4 w-4" />
              Ersten Standort anlegen
            </Button>
          )}
        </Card>
      )}

      {showScanner && <QRScanner onScan={handleQRScan} onClose={() => setShowScanner(false)} />}

      {/* Unknown Code Dialog - Einheitlicher Dialog für unbekannte QR-Codes */}
      <UnknownCodeDialog
        open={showUnknownCodeDialog}
        onOpenChange={setShowUnknownCodeDialog}
        scannedCode={scannedUnknownCode}
        onCreateStandort={() => handleCreateFromUnknown('standort')}
        onCreateAutomat={() => handleCreateFromUnknown('automat')}
        showStandortOption={user?.role === 'admin'}
      />
    </div>
  );
};

export default StandortePage;